import React, { useState, useEffect } from 'react';
import { Users, Building, Award, UserCheck, Book } from 'lucide-react';
import API_URL from '../components/config';
import CompaniesForm from '../components/admin/forms/CompaniesForm'
import TestimonialsForm from '../components/admin/forms/TestimonialsForm'
import ProgramsForm from '../components/admin/forms/ProgramsForm'
import ProgramRow from '../components/admin/rows/ProgramRow';
import CompanyRow from '../components/admin/rows/CompanyRow';
import TestimonialRow from '../components/admin/rows/TestimonialRow';
import axios from "axios";
import DataTable from '../components/admin/DataTable';
import BlogsForm from '../components/admin/forms/BlogsForm';
import BlogRow from '../components/admin/rows/BlogRow';

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('programs');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([])

  useEffect(() => {
    axios.get(`${API_URL}/api/programs`)
      .then((response) => {
        setPrograms(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/testimonials`)
      .then((response) => {
        setTestimonials(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/blogs`)
      .then((response) => {
        setBlogs(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const [companies, setCompanies] = useState([
    { id: 1, name: 'TechCorp Inc', industry: 'Technology', employees: '500+', partnership: 'Gold' },
    { id: 2, name: 'InnovateLab', industry: 'Software', employees: '100-500', partnership: 'Silver' },
    { id: 3, name: 'StartupHub', industry: 'Consulting', employees: '50-100', partnership: 'Bronze' }
  ]);

  const tabs = [
    { id: 'programs', label: 'Programs', icon: Award, data: programs, setData: setPrograms },
    { id: 'companies', label: 'Companies', icon: Building, data: companies, setData: setCompanies },
    { id: 'testimonials', label: 'Testimonials', icon: Users, data: testimonials, setData: setTestimonials },
    {id: 'blogs', label: 'Blogs', icon: Book, data: blogs, setData: setBlogs }
  ];

  const currentTab = tabs.find(tab => tab.id === activeTab);
  const filteredData = currentTab.data.filter(item => 
    Object.values(item).some(value => 
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleCreate = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = async (slug) => {
    console.log('Deleting content with slug:', slug); // <-- Check this log
  
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const endpoint = activeTab; // e.g., 'blogs', 'programs', etc.
        await axios.delete(`${API_URL}/api/${endpoint}/${slug}`);
        // If successful, update local state
        currentTab.setData(prev => prev.filter(item => item.slug !== slug));
      } catch (err) {
        alert('Failed to delete item: ' + err.message);
      }
    }
  };


  const handleSave = async (formData) => {
    const tabApiMap = {
      programs: 'programs',
      companies: 'companies',
      testimonials: 'testimonials',
      blogs: 'blogs'
    };
    const endpoint = tabApiMap[activeTab];
  
    try {
      // Debug logging - remove in production
      console.log('Form data being sent:', formData);
      console.log('Category data:', formData.categories);
      
      if (editingItem) {
        // Update existing item
        const response = await axios.put(`${API_URL}/api/${endpoint}/${editingItem._id}`, formData);
        console.log('Update response:', response.data);
        currentTab.setData(prev => prev.map(item =>
          item._id === editingItem._id ? response.data : item
        ));
      } else {
        // Create new item
        const response = await axios.post(`${API_URL}/api/${endpoint}`, formData);
        console.log('Create response:', response.data);
        currentTab.setData(prev => [...prev, response.data]);
      }
      setShowModal(false);
    } catch (err) {
      console.error('Save error:', err.response?.data || err.message);
      alert("Failed to save: " + (err.response?.data?.message || err.message));
    }
  };
  

  const getTableColumns = () => {
    switch (activeTab) {
      case 'programs':
        return ['Title', 'Category', 'Duration', 'Status', 'Next Cohort', 'Featured'];
      case 'companies':
        return ['Name', 'Industry', 'Employees', 'Partnership'];
      case 'testimonials':
        return ['Name', 'Role', 'Feedback'];
      case 'blogs':
        return ['Title', 'Category', 'Image', 'Featured'];
      default:
        return [];
    }
  };

  const renderTableRow = (item) => {
    const componentMap = {
      programs: ProgramRow,
      companies: CompanyRow,
      testimonials: TestimonialRow,
      blogs: BlogRow,
    };
  
    const RowComponent = componentMap[activeTab];
    return RowComponent ? <RowComponent item={item} /> : null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
      {/* Header */}
      <div className="bg-black shadow-sm border-b py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-200">Admin Dashboard</h1>
              <p className="mt-1 text-sm text-gray-200">Manage your programs, companies, testimonials, and learners</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-200">
                Welcome back, Admin
              </div>
            </div>
          </div>
        </div>
      </div>

      <DataTable
      tabs={tabs}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      onCreate={handleCreate}
      onEdit={handleEdit}
      onDelete={handleDelete}
      getTableColumns={getTableColumns}
      filteredData={filteredData}
      renderTableRow={renderTableRow}
    />
    
      {/* Modal */}
      {showModal && (
        <Modal
          activeTab={activeTab}
          editingItem={editingItem}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

const Modal = ({ activeTab, editingItem, onSave, onClose }) => {
  // Initialize formData with proper defaults
  const initializeFormData = () => {
    if (editingItem) {
      return {
        ...editingItem,
        categories: Array.isArray(editingItem.categories) ? editingItem.categories : [],
        languages: Array.isArray(editingItem.languages) ? editingItem.languages : [],
        featured: editingItem.featured || false
      };
    }
    return {
      categories: [],
      languages: [],
      featured: false
    };
  };

  const [formData, setFormData] = useState(initializeFormData);

  // Update formData when editingItem changes
  useEffect(() => {
    setFormData(initializeFormData());
  }, [editingItem]);

  const languageOptions = ['English', 'Spanish', 'French', 'German', 'Mandarin', 'Japanese'];
  
  const statusOptions = ['Active', 'Draft', 'Inactive', 'Archived'];
  const formatOptions = ['Online', 'In-person', 'Hybrid'];

  const handleCategoryToggle = (category) => {
    const currentCategories = Array.isArray(formData.category) ? formData.category : [];
    const updated = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    
    setFormData(prev => ({ ...prev, category: updated }));
  };

  const handleLanguageToggle = (language) => {
    const currentLanguages = Array.isArray(formData.languages) ? formData.languages : [];
    const updated = currentLanguages.includes(language)
      ? currentLanguages.filter(l => l !== language)
      : [...currentLanguages, language];
    
    setFormData(prev => ({ ...prev, languages: updated }));
  };

  const handleSubmit = () => {
    // Ensure arrays are properly formatted
    const submitData = {
      ...formData,
      category: Array.isArray(formData.category) ? formData.category : [],
      languages: Array.isArray(formData.languages) ? formData.languages : [],
      featured: Boolean(formData.featured)
    };
    
    // Debug logging - remove in production
    console.log('Submitting data:', submitData);
    console.log('Category array:', submitData.category);
    
    onSave(submitData);
  };

  const renderFormFields = () => {
    const formProps = {
      formData,
      setFormData,
      languageOptions,
      statusOptions,
      formatOptions,
      handleCategoryToggle,
      handleLanguageToggle
    };

    switch (activeTab) {
      case 'companies':
        return <CompaniesForm {...formProps} />;
      case 'blogs':
        return <BlogsForm {...formProps} />;
      case 'testimonials':
        return <TestimonialsForm {...formProps} />;
      case 'programs':
        return <ProgramsForm {...formProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
      <div className="w-full max-w-7xl bg-white rounded-lg shadow-xl my-8 mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          {editingItem ? `Edit ${activeTab.slice(0, -1)}` : `Add New ${activeTab.slice(0, -1)}`}
        </h2>
        <div className="space-y-4">
          {renderFormFields()}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {editingItem ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;